import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, MapPin, CalendarDays, Newspaper, Users, HeartHandshake, Link2, AlertTriangle, ListOrdered, Plus, ExternalLink, BookOpen, Loader2, Flame } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import MultilingualText from "@/components/ui/MultilingualText";
import StatusBadge from "@/components/site/StatusBadge";
import { NewsList } from "@/pages/Local";
import { TYPE_LABELS } from "@/pages/Disasters";
import { fileUrl } from "@/lib/files";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const RESOURCES = [
  ["Emergency services", "Call your local emergency number first — 911 (US), 112 (EU/India), 999 (UK), 108 (India disasters)"],
  ["Red Cross / Red Crescent", "https://www.ifrc.org — family links restoration service"],
  ["ICRC — Restoring Family Links", "https://familylinks.icrc.org"],
  ["INTERPOL — Disaster Victim Identification", "https://www.interpol.int — official identification is done only by authorities"],
  ["Local police", "File an official missing-person report as soon as possible"],
];

export default function DisasterPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkId, setLinkId] = useState("");
  const [linking, setLinking] = useState(false);
  const [eventForm, setEventForm] = useState(false);
  const [ev, setEv] = useState({ date: "", title: "", summary: "", source_url: "", source_name: "" });
  const [savingEvent, setSavingEvent] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/v1/disasters/${id}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  async function linkCase(e) {
    e.preventDefault();
    if (!linkId.trim()) return;
    setLinking(true);
    try {
      await api.post(`/v1/disasters/${id}/link`, { case_id: linkId.trim() });
      toast.success("Case linked to this disaster.");
      setLinkId("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLinking(false);
    }
  }

  async function addEvent(e) {
    e.preventDefault();
    if (!ev.date.trim() || !ev.title.trim()) return;
    setSavingEvent(true);
    try {
      await api.post(`/v1/disasters/${id}/events`, {
        date: ev.date.trim(),
        title: ev.title.trim(),
        summary: ev.summary.trim() || undefined,
        source_url: ev.source_url.trim() || undefined,
        source_name: ev.source_name.trim() || undefined,
      });
      toast.success("Event added — marked under review until a moderator confirms it.");
      setEventForm(false);
      setEv({ date: "", title: "", summary: "", source_url: "", source_name: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEvent(false);
    }
  }

  if (loading) return <main className="pt-40 pb-32 text-center text-[color:var(--ink-soft)]">Opening the record…</main>;
  if (!data) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
        <h1 className="heritage-title text-4xl">This record doesn't exist.</h1>
        <Link to="/disasters" className="btn-ghost mt-6 inline-flex" data-testid="disaster-back">
          <ArrowLeft size={14} /> All disasters
        </Link>
      </main>
    );
  }

  const d = data.disaster;
  const stats = data.case_stats || { missing: 0, found: 0, deceased: 0, total: 0 };
  const cases = data.cases || [];
  const memorials = data.memorials || [];
  const news = data.news || [];
  const events = data.events || [];
  const placeSlug = data.place_slug;

  // group timeline events by date (day-by-day)
  const byDay = [];
  for (const e of events) {
    const day = e.date || "Undated";
    const last = byDay[byDay.length - 1];
    if (last && last.day === day) last.items.push(e);
    else byDay.push({ day, items: [e] });
  }

  return (
    <main data-testid="disaster-page" className="pt-32 md:pt-40">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12">
        <Link to="/disasters" className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-[color:var(--ink-soft)] link-underline mb-8" data-testid="disaster-back-list">
          <ArrowLeft size={12} /> <span>All disasters</span>
        </Link>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="px-2 py-0.5 font-heritage-caps text-[10px] tracking-[0.14em] bg-[color:var(--sepia)] text-[color:var(--parchment)]" data-testid="disaster-type">
            {TYPE_LABELS[d.type] || d.type}
          </span>
          <span className="numeric-index text-[color:var(--ink-soft)] uppercase" data-testid="disaster-status">{d.status}</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(40px, 6vw, 100px)" }}
          data-testid="disaster-name"
        >
          {d.name}
        </motion.h1>
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[color:var(--ink-soft)]">
          <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {[d.city, d.region, d.country].filter(Boolean).join(", ")}</span>
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> began {d.start_date}{d.end_date ? ` · ended ${d.end_date}` : ""}</span>
        </div>
        {d.overview && (
          <MultilingualText value={d.overview} className="mt-6 max-w-3xl text-[17px] leading-[1.7] text-[color:var(--ink-soft)]" testid="disaster-overview-text" />
        )}
      </section>

      {/* Living statistics — always traceable to the linked cases below */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-14" data-testid="disaster-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[color:var(--ink-line)]">
          {[
            ["Missing", stats.missing, "disaster-stat-missing"],
            ["Found safe / located", stats.found, "disaster-stat-found"],
            ["Confirmed deceased", stats.deceased, "disaster-stat-deceased"],
            ["Linked cases", stats.total, "disaster-stat-total"],
          ].map(([label, val, tid]) => (
            <div key={tid} className="border-r border-b border-[color:var(--ink-line)] p-6 text-center" data-testid={tid}>
              <div className="font-serif-display text-5xl leading-none tabular-nums">{val}</div>
              <div className="overline text-[10px] text-[color:var(--ink-soft)] mt-2">{label}</div>
            </div>
          ))}
        </div>
        <p className="mt-2 numeric-index text-[color:var(--ink-soft)]">Counts come only from the cases linked below — never estimated.</p>
      </section>

      {/* Day-by-day sourced timeline */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-timeline">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
            <ListOrdered size={18} className="text-[color:var(--sepia)]" /> Day by day
          </h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        {byDay.length === 0 ? (
          <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)]" data-testid="disaster-timeline-empty">
            No timeline entries yet — the hourly news scan adds sourced events automatically.
          </p>
        ) : (
          <ol className="max-w-3xl mx-auto space-y-10">
            {byDay.map((g, gi) => (
              <li key={g.day} data-testid={`disaster-day-${gi}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="font-serif-display text-xl text-[color:var(--rust)] whitespace-nowrap">{g.day}</div>
                  <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
                </div>
                <ol className="space-y-4 border-l-2 border-[color:var(--sepia)]/30 ml-2 pl-6">
                  {g.items.map((e, ei) => (
                    <li key={e.id} data-testid={`disaster-event-${gi}-${ei}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[15px] leading-snug">{e.title}</span>
                        <span className={`border px-1.5 py-px font-heritage-caps text-[9px] tracking-[0.12em] ${
                          e.verification === "VERIFIED" || e.verification === "OFFICIAL"
                            ? "border-[#3d5a3c] text-[#3d5a3c]"
                            : e.verification === "SOURCE_REPORT"
                              ? "border-[color:var(--sepia)] text-[color:var(--sepia)]"
                              : "border-[color:var(--rust)]/60 text-[color:var(--rust)]"
                        }`}>
                          {e.verification === "SOURCE_REPORT" ? "Source report" : e.verification === "UNDER_REVIEW" ? "Under review" : e.verification === "OFFICIAL" ? "Official" : e.verification === "VERIFIED" ? "Verified" : "Unverified"}
                        </span>
                      </div>
                      {e.summary && <p className="mt-1 text-sm text-[color:var(--ink-soft)] leading-relaxed">{e.summary}</p>}
                      {e.source_url && (
                        <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 whitespace-nowrap text-xs link-underline text-[color:var(--sepia)]" data-testid={`disaster-event-source-${gi}-${ei}`}>
                          {e.source_name || "Source"}&nbsp;<ExternalLink size={10} />
                        </a>
                      )}
                      {user?.role === "admin" && (
                        <button
                          onClick={async () => {
                            try {
                              await api.delete(`/v1/disasters/${id}/events/${e.id}`);
                              toast.success("Event removed.");
                              load();
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          data-testid={`disaster-event-delete-${gi}-${ei}`}
                          className="ml-3 text-[10px] link-underline text-[color:var(--ink-soft)] hover:text-[color:var(--rust)]"
                        >
                          remove
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        )}
        {user && (
          <div className="mt-8 text-center">
            {eventForm ? (
              <form onSubmit={addEvent} className="max-w-xl mx-auto border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 text-left" data-testid="disaster-event-form">
                <div className="overline mb-4">Add a day to the record</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input data-testid="disaster-event-date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} placeholder="Date — 2026-08-10" className="bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none" required />
                  <input data-testid="disaster-event-source-name" value={ev.source_name} onChange={(e) => setEv({ ...ev, source_name: e.target.value })} placeholder="Source — Reuters, district office…" className="bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none" />
                </div>
                <input data-testid="disaster-event-title" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} placeholder="What happened that day" className="mt-4 w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none" required />
                <input data-testid="disaster-event-source-url" value={ev.source_url} onChange={(e) => setEv({ ...ev, source_url: e.target.value })} placeholder="Source link (https://…)" className="mt-4 w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none" />
                <div className="mt-5 flex gap-3">
                  <button type="submit" disabled={savingEvent} data-testid="disaster-event-submit" className="btn-wood !py-2 !px-4 !text-xs disabled:opacity-50">
                    {savingEvent ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add to timeline
                  </button>
                  <button type="button" onClick={() => setEventForm(false)} className="btn-ghost !py-2 !px-4 !text-xs">Cancel</button>
                </div>
              </form>
            ) : (
              <button onClick={() => setEventForm(true)} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="disaster-event-open">
                <Plus size={12} /> Add a sourced event
              </button>
            )}
          </div>
        )}
      </section>

      {/* More about the place */}
      {placeSlug && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-place">
          <Link
            to={`/places/${placeSlug}`}
            data-testid="disaster-place-link"
            className="group flex items-center gap-4 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 max-w-2xl mx-auto"
          >
            <BookOpen size={20} className="text-[color:var(--sepia)] shrink-0" />
            <div>
              <div className="font-serif-display text-xl group-hover:text-[color:var(--rust)] transition-colors">
                More about {d.region || d.city || d.country}
              </div>
              <div className="text-sm text-[color:var(--ink-soft)]">The place, its history, and what family seekers find there.</div>
            </div>
          </Link>
        </section>
      )}

      {/* Linked cases */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-cases">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
            <Users size={18} className="text-[color:var(--sepia)]" /> People connected to this disaster
          </h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        {cases.length === 0 ? (
          <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)]" data-testid="disaster-cases-empty">
            No cases linked yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {cases.map((c) => (
              <Link key={c.case_id} to={`/missing/case/${c.case_id}`} data-testid={`disaster-case-${c.case_id}`} className="group block border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)]">
                <div className="aspect-[4/5] overflow-hidden bg-[color:var(--parchment-2)] flex items-center justify-center">
                  {c.photo_path ? (
                    <img src={fileUrl(c.photo_path)} alt={c.name} className="w-full h-full object-cover" style={{ filter: "sepia(15%)" }} />
                  ) : (
                    <span className="font-serif-display text-5xl text-[color:var(--sepia)]/40">{c.name?.[0]}</span>
                  )}
                </div>
                <div className="p-4">
                  <StatusBadge status={c.status} />
                  <h3 className="mt-2 font-serif-display text-lg leading-snug group-hover:text-[color:var(--rust)] transition-colors">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        )}
        {user && (
          <form onSubmit={linkCase} className="mt-8 flex flex-wrap items-center gap-3 max-w-xl mx-auto" data-testid="disaster-link-form">
            <Link2 size={14} className="text-[color:var(--sepia)]" />
            <input
              data-testid="disaster-link-input"
              value={linkId}
              onChange={(e) => setLinkId(e.target.value)}
              placeholder="Link a case you administer — e.g. FT-MP-2026-000001"
              className="flex-1 min-w-[240px] bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none focus:border-[color:var(--rust)]"
            />
            <button type="submit" disabled={linking || !linkId.trim()} data-testid="disaster-link-submit" className="btn-ghost !py-2 !px-4 !text-xs disabled:opacity-50">
              Link case
            </button>
          </form>
        )}
      </section>

      {/* In Memoriam — public memorials connected to this disaster */}
      {memorials.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-memorials">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
              <Flame size={18} className="text-[color:var(--rust)]" /> In memoriam
            </h2>
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          </div>
          <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)] mb-8 max-w-xl mx-auto">
            Families have connected these memorials to this record — lives remembered, not numbers.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {memorials.map((m) => {
              const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
              const years = [m.birth_date, m.death_date].filter(Boolean).join(" – ");
              return (
                <Link
                  key={m.memorial_id}
                  to={`/memorial/${m.memorial_id}`}
                  data-testid={`disaster-memorial-${m.memorial_id}`}
                  className="group block border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)]"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[color:var(--parchment-2)] flex items-center justify-center">
                    {m.photo_url ? (
                      <img src={fileUrl(m.photo_url)} alt={name} className="w-full h-full object-cover" style={{ filter: "sepia(15%)" }} />
                    ) : (
                      <span className="font-serif-display text-5xl text-[color:var(--sepia)]/40">{m.first_name?.[0] || "·"}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif-display text-lg leading-snug group-hover:text-[color:var(--rust)] transition-colors">{name}</h3>
                    {years && <div className="mt-1 numeric-index text-[color:var(--ink-soft)]">{years}</div>}
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[color:var(--sepia)]">
                      <Flame size={10} className="text-[color:var(--rust)]" /> {m.candle_count} candle{m.candle_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* News */}
      {news.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-news">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
              <Newspaper size={18} className="text-[color:var(--sepia)]" /> What the news is reporting
            </h2>
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          </div>
          <NewsList news={news} testid="disaster-news-list" />
          <p className="mt-3 numeric-index text-[color:var(--ink-soft)]">Headlines via Google News · each opens the original publisher</p>
        </section>
      )}

      {/* Resources */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16" data-testid="disaster-resources">
        <div className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 md:p-8">
          <div className="overline text-[color:var(--sepia)] mb-5 flex items-center gap-2">
            <HeartHandshake size={13} /> Help &amp; official resources
          </div>
          <ol className="grid md:grid-cols-2 gap-x-10 gap-y-4">
            {RESOURCES.map(([label, text], i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`disaster-resource-${i}`}>
                <span className="numeric-index text-[color:var(--sepia)] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span><strong>{label}.</strong> {text}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="border-t border-[color:var(--sepia)]/20 bg-[color:var(--parchment-2)]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 text-center">
          <AlertTriangle size={16} className="mx-auto text-[color:var(--sepia)]" />
          <p className="mt-3 text-xs leading-relaxed text-[color:var(--ink-soft)] max-w-2xl mx-auto">
            FamilyTree.Team documents and connects information — it is not an emergency service, and official
            identification of victims is done only by authorities (INTERPOL DVI). When reports conflict,
            we show the conflict rather than choosing a number.
          </p>
        </div>
      </section>
    </main>
  );
}
